import { CompanyDTO, CompanyTag } from "@shaarp/common";
import { useSyncExternalStore } from "react";

type CompanyPreviewPanelEntryBase = {
  /**
   * Stable per-entry identity. Collapses consecutive duplicates (the same
   * company cited back-to-back) into one stop and matches the chip currently
   * shown. For chat mentions it is the key the web profile is filed under; for
   * table rows, the company id.
   */
  key: string;
  /**
   * DOM ordinal of the chip this entry maps to among the conversation's mention
   * chips, so the panel scrolls back to the exact occurrence the user opened —
   * not the first mention of that company. Absent for the table, where each
   * company appears once.
   */
  anchor?: number;
};

/**
 * One navigable stop shown in the side panel, complete the moment it is opened:
 * a table row carries its company, a chat mention carries what the assistant
 * said about the company (its tag) and is shown as a web preview.
 */
export type CompanyPreviewPanelEntry =
  | (CompanyPreviewPanelEntryBase & { company: CompanyDTO; tag?: never })
  | (CompanyPreviewPanelEntryBase & { company: null; tag: CompanyTag });

/**
 * Where the panel renders. "sheet" is the default, viewport-anchored side sheet
 * (the table, the main chat page, the company-page modal chat). "inline" tells
 * the singleton sheet to stand down and lets a host container render the panel
 * in its own bounds instead — used inside the company-page chat when docked as a
 * side panel, so the company preview lives within the chat's own component
 * rather than floating over it as a differently sized sheet.
 */
export type CompanyPreviewPanelSurface = "sheet" | "inline";

/** Id of the company the panel shows, or null when closed or still resolving. */
export const companyOnShowId = (
  state: CompanyPreviewPanelState | null,
): string | null => state?.entries[state.index]?.company?.id ?? null;

export type CompanyPreviewPanelState = {
  entries: CompanyPreviewPanelEntry[];
  index: number;
  /**
   * Whether the panel grabs the page like a modal (dark overlay, focus trap,
   * scroll lock). Triggers that must stay interactive while the panel is open
   * — the chat, for instance — open it non-modally.
   */
  modal: boolean;
  /** Rendering surface — see {@link CompanyPreviewPanelSurface}. */
  surface: CompanyPreviewPanelSurface;
};

/**
 * Nearest navigable stop from `from` in `direction`: the closest entry whose
 * company differs from the one at `from`. Consecutive mentions of the same
 * company (same key) collapse into one stop, so the chevrons only ever land on
 * a distinct company.
 */
function findStop(
  entries: CompanyPreviewPanelEntry[],
  from: number,
  direction: 1 | -1,
): number | null {
  const fromKey = entries[from]?.key;
  for (let i = from + direction; i >= 0 && i < entries.length; i += direction) {
    if (entries[i].key !== fromKey) return i;
  }
  return null;
}

function canGoPrevious(state: CompanyPreviewPanelState | null): boolean {
  return state ? findStop(state.entries, state.index, -1) !== null : false;
}

function canGoNext(state: CompanyPreviewPanelState | null): boolean {
  return state ? findStop(state.entries, state.index, 1) !== null : false;
}

/**
 * Whether the entry keyed `key` is the one currently shown in the panel.
 * Returning a boolean rather than the raw state lets each chip subscribe to
 * just its own selection status: React bails out of re-rendering (via
 * `Object.is`) when the boolean hasn't changed, instead of every mention in
 * the conversation re-rendering on every store update.
 */
export function isEntrySelected(
  state: CompanyPreviewPanelState | null,
  key: string,
): boolean {
  return !!key && state?.entries[state.index]?.key === key;
}

/**
 * Module-level store tracking the company shown in the side panel.
 * Keeping it outside React lets the panel persist across remounts of the
 * triggers that open it (table cell re-renders, chat re-renders, ...).
 *
 * The store keeps the full ordered list of stops the conversation cites, so
 * the chevrons can step across every cited company.
 */
export const companyPreviewPanelStore = {
  state: null as CompanyPreviewPanelState | null,
  listeners: new Set<() => void>(),
  getSnapshot(): CompanyPreviewPanelState | null {
    return companyPreviewPanelStore.state;
  },
  subscribe(listener: () => void): () => void {
    companyPreviewPanelStore.listeners.add(listener);
    return () => companyPreviewPanelStore.listeners.delete(listener);
  },
  emit(): void {
    companyPreviewPanelStore.listeners.forEach((l) => l());
  },
  /** Opens a list of companies already in hand (the table). */
  open(
    company: CompanyDTO,
    companies: CompanyDTO[],
    options?: { modal?: boolean; surface?: CompanyPreviewPanelSurface },
  ): void {
    const entries: CompanyPreviewPanelEntry[] = companies.map((c) => ({
      key: c.id,
      company: c,
    }));
    const found = entries.findIndex((entry) => entry.key === company.id);
    companyPreviewPanelStore.state = {
      entries,
      index: found >= 0 ? found : 0,
      modal: options?.modal ?? true,
      surface: options?.surface ?? "sheet",
    };
    companyPreviewPanelStore.emit();
  },
  /**
   * Opens at an explicit position in an ordered list of stops — used by the chat,
   * whose list has one entry per visible mention (duplicates included) so opening
   * the clicked occurrence anchors the panel to that exact chip.
   */
  openAt(
    entries: CompanyPreviewPanelEntry[],
    index: number,
    options?: { modal?: boolean; surface?: CompanyPreviewPanelSurface },
  ): void {
    companyPreviewPanelStore.state = {
      entries,
      index,
      modal: options?.modal ?? true,
      surface: options?.surface ?? "sheet",
    };
    companyPreviewPanelStore.emit();
  },
  previous(): void {
    step(-1);
  },
  next(): void {
    step(1);
  },
  close(): void {
    companyPreviewPanelStore.state = null;
    companyPreviewPanelStore.emit();
  },
};

/**
 * Subscribes to {@link companyPreviewPanelStore} and derives the current entry's view
 * — the shared read model behind both surfaces (the viewport sheet and the
 * inline panel), so the two never drift in how they read the store.
 */
export function useCompanyPreviewPanelEntry(): {
  state: CompanyPreviewPanelState | null;
  entry: CompanyPreviewPanelEntry | null;
  company: CompanyDTO | null;
  /** The tag of a mention opened as a web preview (null for a table row). */
  tag: CompanyTag | null;
  /**
   * Whether the panel has something to show — every entry carries a company or
   * a tag, so this is whether there is a current entry. The single condition
   * both surfaces open on, and what anything placed around the panel should read.
   */
  isVisible: boolean;
  hasPrevious: boolean;
  hasNext: boolean;
  /**
   * Whether the panel holds more than one navigable company — i.e. stepping in
   * either direction lands somewhere. False when a single company is being
   * previewed, so the surfaces hide the prev/next chevrons entirely (there is
   * nowhere to go) rather than showing them permanently disabled.
   */
  hasMultipleStops: boolean;
} {
  const state = useSyncExternalStore(
    companyPreviewPanelStore.subscribe,
    companyPreviewPanelStore.getSnapshot,
  );
  const entry = state ? (state.entries[state.index] ?? null) : null;
  const company = entry?.company ?? null;
  const tag = entry?.tag ?? null;
  const hasPrevious = canGoPrevious(state);
  const hasNext = canGoNext(state);
  return {
    state,
    entry,
    company,
    tag,
    isVisible: entry !== null,
    hasPrevious,
    hasNext,
    hasMultipleStops: hasPrevious || hasNext,
  };
}

/** Steps the panel to the next/previous distinct company, if there is one. */
function step(direction: 1 | -1): void {
  const current = companyPreviewPanelStore.state;
  if (!current) return;
  const target = findStop(current.entries, current.index, direction);
  if (target === null) return;
  companyPreviewPanelStore.state = { ...current, index: target };
  companyPreviewPanelStore.emit();
}
